// EvIcon.tsx — the stroke icon set from the approved prototype (events-live/
// tokens.jsx), ported verbatim so the volunteer-loop surfaces use the exact
// glyphs and accept a `color` prop. The app's main Icon has a different API
// and name set, so the volunteer feature carries its own.

import type { CSSProperties } from 'react'

type PathItem = string | null | { circle?: [number, number, number]; rect?: [number, number, number, number, number]; fill?: string }

const PATHS: Record<string, PathItem[]> = {
  clock: ['M12 7v5l3 2', null, { circle: [12, 12, 9] }],
  check: ['M20 6 9 17l-5-5'],
  checkCircle: ['m9 12 2 2 4-4', null, { circle: [12, 12, 9] }],
  chevron: ['m9 6 6 6-6 6'],
  chevronDown: ['m6 9 6 6 6-6'],
  arrowRight: ['M5 12h14', 'm13 6 6 6-6 6'],
  phone: ['M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z'],
  message: ['M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z'],
  users: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75', null, { circle: [9, 7, 4] }],
  user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', null, { circle: [12, 7, 4] }],
  calendar: ['M16 2v4', 'M8 2v4', 'M3 10h18', null, { rect: [3, 4, 18, 18, 2] }],
  alert: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z', 'M12 9v4', 'M12 17h.01'],
  mapPin: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z', null, { circle: [12, 10, 3] }],
  shield: ['M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6z'],
  shieldCheck: ['M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6z', 'm9 12 2 2 4-4'],
  signout: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'm16 17 5-5-5-5', 'M21 12H9'],
  plus: ['M12 5v14', 'M5 12h14'],
  x: ['M18 6 6 18', 'm6 6 12 12'],
  swap: ['M16 3l4 4-4 4', 'M20 7H4', 'M8 21l-4-4 4-4', 'M4 17h16'],
  grip: [null, { circle: [9, 6, 1] }, { circle: [15, 6, 1] }, { circle: [9, 12, 1] }, { circle: [15, 12, 1] }, { circle: [9, 18, 1] }, { circle: [15, 18, 1] }],
  info: ['M12 16v-4', 'M12 8h.01', null, { circle: [12, 12, 9] }],
  star: ['M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14l-5-4.87 6.91-1.01L12 2Z'],
  bell: ['M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0'],
  train: ['M8 3.1V7a4 4 0 0 0 8 0V3.1', 'm9 15-1.5 3', 'm15 15 1.5 3', 'M4 13h16', null, { rect: [4, 3, 16, 13, 4] }, { circle: [8.5, 11.5, 0.6] }, { circle: [15.5, 11.5, 0.6] }],
  clipboard: ['M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2', 'm9 14 2 2 4-4', null, { rect: [8, 2, 8, 4, 1] }],
  send: ['M22 2 11 13', 'M22 2 15 22l-4-9-9-4 20-7Z'],
  filter: ['M22 3H2l8 9.46V19l4 2v-8.54L22 3z'],
  layers: ['m12 2 9 5-9 5-9-5 9-5Z', 'm3 12 9 5 9-5', 'm3 17 9 5 9-5'],
  grid: [null, { rect: [3, 3, 7, 7, 1] }, { rect: [14, 3, 7, 7, 1] }, { rect: [14, 14, 7, 7, 1] }, { rect: [3, 14, 7, 7, 1] }],
  heart: ['M19 14c1.5-1.5 3-3.5 3-5.5A4.5 4.5 0 0 0 17.5 4 6.4 6.4 0 0 0 12 7a6.4 6.4 0 0 0-5.5-3A4.5 4.5 0 0 0 2 8.5c0 2 1.5 4 3 5.5l7 7Z'],
  edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5Z'],
  doc: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M9 13h6', 'M9 17h6'],
}

export function EvIcon({ name, size = 18, color = 'currentColor', strokeWidth = 1.8, style }: {
  name: string
  size?: number
  color?: string
  strokeWidth?: number
  style?: CSSProperties
}) {
  const spec = PATHS[name] || PATHS.info!
  const els: React.ReactNode[] = []
  let k = 0
  for (const item of spec) {
    if (item == null) continue
    if (typeof item === 'string') { els.push(<path key={k++} d={item} />); continue }
    if (item.circle) { const [cx, cy, r] = item.circle; els.push(<circle key={k++} cx={cx} cy={cy} r={r} fill={item.fill || 'none'} />) }
    if (item.rect) { const [x, y, w, h, rx] = item.rect; els.push(<rect key={k++} x={x} y={y} width={w} height={h} rx={rx} />) }
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style} aria-hidden="true">
      {els}
    </svg>
  )
}
